import React, { useState } from 'react'
import { Button, Checkbox, Col, Flex, Form, Row, Select, Tooltip, Typography } from 'antd'
import { Controller } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import { ResetIcon, ResetIconDisabled } from 'src/assets/icons'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'

import './index.scss'
import { ExclamationCircleOutlined } from '@ant-design/icons'

interface Props {
  errors: any
  control: any
  dirtyFields: any
  fileContainsIssuedDate: boolean
  dateInFileDontIncludeSeparators: boolean
  columnList: any
  getValues: any
  resetMappingFields: () => void
  enableResetBtn?: boolean
  selectedColumns: any
  setSelectedColumns: any
  resetDateFormat?: () => void
}

function MappingFields(props: Props) {
  const { t } = useTranslation()

  const lookupValues = useSelector(getLookupValues)

  const { DateFormat, DateFormatWithSeparator } = lookupValues || {}

  const {
    errors,
    control,
    fileContainsIssuedDate,
    dateInFileDontIncludeSeparators,
    columnList,
    resetMappingFields,
    enableResetBtn = false,
    selectedColumns,
    setSelectedColumns,
    resetDateFormat,
  } = props

  const filteredColumnList = (fieldName: string) => {
    const selectedValue = Object.values(selectedColumns)?.filter(
      (item: any) => item !== null && selectedColumns[fieldName] !== item,
    )

    const selectedLabel = selectedValue?.map((item: any) => getLabelSelection(item))
    return columnList?.filter(
      (item: any) =>
        !selectedValue?.includes(item?.value) && item?.label !== '' && !selectedLabel?.includes(item?.label),
    )
  }

  const getLabelSelection = (value: any) => {
    return columnList?.find((item: any) => item?.value === Number(value))?.label
  }

  const isResetDisabled = !enableResetBtn

  return (
    <>
      <Flex className='mapping-fields-header'>
        <Typography className='mapping-fields-title'>
          {t('FileMappingSetup:addFileMapping.title.mappingFields')}
        </Typography>

        <Button
          className='reset-button'
          onClick={() => {
            resetMappingFields()
          }}
          disabled={isResetDisabled}
        >
          {isResetDisabled ? <ResetIconDisabled /> : <ResetIcon />}
          {t('OverallFunctionality:reset_btn')}
        </Button>
      </Flex>
      <Flex vertical className='mapping-fields-container'>
        <Row gutter={24} align='middle'>
          <Col span={24}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.checkNumber')} className='isRequired'>
              <Controller
                name='checkNumber'
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={t('FileMappingSetup:addFileMapping.placeholder.selectColumn')}
                    options={filteredColumnList('checkNumber')}
                    onChange={(e) => {
                      field?.onChange(getLabelSelection(e))
                      setSelectedColumns((prev: any) => ({ ...prev, checkNumber: e }))
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='checkNumber' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24} align='middle'>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.amount')} className='isRequired'>
              <Controller
                name='amount'
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={t('FileMappingSetup:addFileMapping.placeholder.selectColumn')}
                    options={filteredColumnList('amount')}
                    onChange={(e) => {
                      field?.onChange(getLabelSelection(e))
                      setSelectedColumns((prev: any) => ({ ...prev, amount: e }))
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='amount' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24} align='middle'>
          {fileContainsIssuedDate && (
            <Col span={12}>
              <Form.Item label={t('FileMappingSetup:addFileMapping.label.issuedDate')} className='isRequired'>
                <Controller
                  name='issuedDate'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Select
                      {...field}
                      status={!!fieldState.error ? 'error' : ''}
                      placeholder={t('FileMappingSetup:addFileMapping.placeholder.selectColumn')}
                      options={filteredColumnList('issuedDate')}
                      onChange={(e) => {
                        field?.onChange(getLabelSelection(e))
                        setSelectedColumns((prev: any) => ({ ...prev, issuedDate: e }))
                      }}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='issuedDate' />
                </Typography>
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.datesInFile')} className='isRequired'>
              <Row gutter={8}>
                <Col span={5}>
                  <Controller
                    name='dateInFileDontIncludeSeparators'
                    control={control}
                    render={({ field, fieldState }) => (
                      <Select
                        {...field}
                        status={!!fieldState.error ? 'error' : ''}
                        options={[
                          { value: false, label: 'No' },
                          { value: true, label: 'Yes' },
                        ]}
                        onChange={(e) => {
                          resetDateFormat?.()
                          field.onChange(e)
                        }}
                        disabled={!fileContainsIssuedDate}
                      />
                    )}
                  />
                </Col>
                <Col span={19}>
                  <Controller
                    name='issuedDateFormat'
                    control={control}
                    render={({ field, fieldState }) => (
                      <Select
                        {...field}
                        status={!!fieldState.error ? 'error' : ''}
                        placeholder={t('FileMappingSetup:addFileMapping.placeholder.issuedDateFormat')}
                        options={dateInFileDontIncludeSeparators ? DateFormatWithSeparator : DateFormat}
                        disabled={!fileContainsIssuedDate} // !dateInFileDontIncludeSeparators
                      />
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='issuedDateFormat' />
                  </Typography>
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={
                <Tooltip
                  placement='right'
                  title={t('FileMappingSetup:addFileMapping.tooltip.accountNumber')}
                  color='white'
                  trigger='hover'
                  arrow={false}
                >
                  <Flex gap={9}>
                    {t('FileMappingSetup:addFileMapping.label.accountNumber')}
                    <ExclamationCircleOutlined />
                  </Flex>
                </Tooltip>
              }
            >
              <Controller
                name='accountNumber'
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={t('FileMappingSetup:addFileMapping.placeholder.selectColumn')}
                    options={filteredColumnList('accountNumber')}
                    onChange={(e) => {
                      field?.onChange(getLabelSelection(e))
                      setSelectedColumns((prev: any) => ({ ...prev, accountNumber: e }))
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='accountNumber' />
              </Typography>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.issuedPayee')}>
              <Controller
                name='issuedPayee'
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={t('FileMappingSetup:addFileMapping.placeholder.selectColumn')}
                    options={filteredColumnList('issuedPayee')}
                    onChange={(e) => {
                      field?.onChange(getLabelSelection(e))
                      setSelectedColumns((prev: any) => ({ ...prev, issuedPayee: e }))
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='issuedPayee' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
      </Flex>
    </>
  )
}

export default MappingFields
